import React, { useState, useMemo } from 'react';
import {
    Form,
    Radio,
    Select,
    Input,
    Space,
    Spin,
    message,
    Alert,
    Typography,
    Row,
    Col,
    Divider
} from 'antd';
import { BookOutlined, AimOutlined } from '@ant-design/icons';
import { debounce } from 'lodash-es';
import * as lessonPlanApi from '../services/lessonPlanApi';
import SyllabusTopicConceptSelect, { SELECT_LEVELS } from '../../common/SyllabusTopicConceptSelectB';
import { getIcon } from '../utils/iconMap';

const { Option } = Select;
const { Title } = Typography;

const LearningObjective = ({ topicSource, options }) => {
    // --- Logic from LearningTopic.js ---
    const form = Form.useFormInstance();
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchResults, setSearchResults] = useState([]);

    const handleAcaraSearch = useMemo(() =>
            debounce(async (query) => {
                if (!query || query.length < 3) {
                    setSearchResults([]);
                    return;
                }
                setSearchLoading(true);
                try {
                    const results = await lessonPlanApi.searchAcaraCodes(query);
                    setSearchResults(results || []);
                } catch (err) {
                    message.error(err.message || "Failed to search ACARA codes.");
                    setSearchResults([]);
                } finally {
                    setSearchLoading(false);
                }
            }, 500),
        []
    );

    const handleAcaraSelect = (selectedCode) => {
        const selectedStandard = searchResults.find(r => r.code === selectedCode);
        if (selectedStandard) {
            form.setFieldValue('acaraDescription', selectedStandard.description);
        } else {
            form.setFieldValue('acaraDescription', undefined);
        }
    };

    const handleConceptChange = (activeObject) => {
        console.log('Syllabus/Topic/Concept selection changed, active object:', activeObject);
    };

    const initialSyllabusIds = useMemo(() => {
        if (!form) return null;
        const { syllabusGroupId, syllabusId, topicId, conceptId } = form.getFieldsValue(true);
        if (syllabusGroupId || syllabusId || topicId || conceptId) {
            return { syllabusGroupId, syllabusId, topicId, conceptId };
        }
        return null;
    }, [form]);

    const handleSyllabusLoadComplete = (state) => {
        if (!state) return;
        console.log('handleSyllabusLoadComplete', state);
    };

    // --- Logic from LearningGoal.js ---
    const { learningGoals = [], knowledgeDimensions = [], timeframeOptions = [], dokLevels = [] } = options || {};

    return (
        <Space direction="vertical" style={{ width: '100%' }} size="large">

            {/* --- START: Content from LearningTopic.js --- */}
            <Title level={5}><BookOutlined /> Learning Topic</Title>
            <Form.Item
                label="How do you want to define your topic?"
                name="topicSource"
                rules={[{ required: true, message: 'Please select a topic source.' }]}
            >
                <Radio.Group buttonStyle="solid">
                    <Radio.Button value="TEXTBOOK">From Textbook</Radio.Button>
                    <Radio.Button value="ACARA_CODE">By ACARA Code</Radio.Button>
                </Radio.Group>
            </Form.Item>

            {topicSource === 'TEXTBOOK' && (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Alert
                        message="Select a syllabus, topic, and concept to ground the AI generation in your curriculum."
                        type="info"
                        showIcon
                    />
                    <SyllabusTopicConceptSelect
                        form={form}
                        selectLevel={SELECT_LEVELS.CONCEPT}
                        onChange={handleConceptChange}
                        onLoadComplete={handleSyllabusLoadComplete}
                        initialIds={initialSyllabusIds}
                    />
                </Space>
            )}

            {topicSource === 'ACARA_CODE' && (
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Alert
                        message="Search for an ACARA code or keyword to ground the AI generation in a specific standard."
                        type="info"
                        showIcon
                    />
                    <Form.Item
                        label="Search & Select Standard"
                        name="acaraCode"
                        rules={[{ required: true, message: 'Please select a standard.' }]}
                    >
                        <Select
                            placeholder="e.g., AC9M8N01 or 'Pythagoras'"
                            loading={searchLoading}
                            onChange={handleAcaraSelect}
                            showSearch
                            filterOption={false}
                            onSearch={handleAcaraSearch}
                            notFoundContent={searchLoading ? <Spin size="small" /> : null}
                            allowClear
                        >
                            {searchResults.map(r => (
                                <Option key={r.code} value={r.code}>
                                    <strong>{r.code}</strong> {r.strand ? `${r.strand}: ` : ''}{r.description}
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="acaraDescription" hidden><Input /></Form.Item>
                </Space>
            )}
            {/* --- END: Content from LearningTopic.js --- */}

            <Divider />

            {/* --- START: Content from LearningGoal.js --- */}
            <Title level={5}><AimOutlined /> Learning Goal</Title>
            <Form.Item label="What is the main purpose of this lesson?" name="learningGoal" rules={[{ required: true }]}>
                <Radio.Group optionType="button" buttonStyle="solid" style={{ width: '100%' }}>
                    <Row gutter={[16, 16]}>{learningGoals.map((item) => (
                        <Col xs={24} md={8} key={item.value}>
                            <Radio.Button value={item.value} style={{ width: '100%', height: '100%', padding: '16px' }}>
                                <Space direction="vertical" align="center" style={{ width: '100%' }}>
                                    {React.cloneElement(getIcon(item.icon), { style: { fontSize: '24px' } })}
                                    <span>{item.label}</span>
                                </Space>
                            </Radio.Button>
                        </Col>
                    ))}</Row>
                </Radio.Group>
            </Form.Item>
            <Form.Item label="What is the primary type of knowledge?" name="knowledgeDimension" rules={[{ required: true }]}>
                <Radio.Group optionType="button" buttonStyle="solid" style={{ width: '100%' }}>
                    <Space direction="vertical" style={{ width: '100%' }}>
                        {knowledgeDimensions.map((item) => (
                            <Radio.Button key={item.value} value={item.value} style={{ width: '100%', height: 'auto', padding: '12px', textAlign: 'left' }}>
                                <Space align="center">
                                    {React.cloneElement(getIcon(item.icon), { style: { fontSize: '20px' } })}
                                    <div>
                                        <strong>{item.label.split(':')[0]}:</strong>
                                        <span>{item.label.split(':')[1]}</span>
                                    </div>
                                </Space>
                            </Radio.Button>
                        ))}
                    </Space>
                </Radio.Group>
            </Form.Item>
            <Form.Item label="What is the main cognitive task for students?" name="dokLevel" rules={[{ required: true }]}>
                <Radio.Group optionType="button" buttonStyle="solid" style={{ width: '100%' }}>
                    <Row gutter={[12, 12]}>{dokLevels.map((item) => (
                        <Col xs={24} md={12} key={item.value}><Radio.Button value={item.value} style={{ width: '100%', height: '100%', padding: '12px' }}><Space>{getIcon(item.icon)}<span>{item.label}</span></Space></Radio.Button></Col>
                    ))}</Row>
                </Radio.Group>
            </Form.Item>
            {/* --- END: Content from LearningGoal.js --- */}
        </Space>
    );
};

export default LearningObjective;